"use client";
import { useGetAgentWidgetConfigQuery } from "../../apis/agent.api";
import { AnimatePresence, motion } from "framer-motion";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { WidgetScene } from "../types/widget-scenes";
import Choice from "./choice";
import ChoiceChat from "./choice-chat";
import IncomingCall from "./incoming-call";
import "./widget.css";
import StoreProvider from "../providers/store-provider";
const ChoiceCall = dynamic(() => import("./choice-call"), { ssr: false });

export type WidgetPosition =
  | "bottom-left"
  | "bottom-right"
  | "top-left"
  | "top-right";

export const Widget = ({
  position = "bottom-right",
  agentid,
  isPreview,
  className,
}: {
  position?: WidgetPosition;
  agentid: string;
  isPreview?: boolean;
  className?: string;
}) => {
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const [widgetStage, setWidgetStage] = useState<WidgetScene>(
    WidgetScene.LOADING
  );
  const { data: config } = useGetAgentWidgetConfigQuery(agentid);

  useEffect(() => {
    const timer = setTimeout(() => {
      setWidgetStage(WidgetScene.CHOICE);
    }, 2000);

    const handleWidgetOpen = (event: CustomEvent) => {
      if (event.detail?.scene) {
        setWidgetStage(event.detail.scene);
      }
    };

    const widgetElement = document.querySelector("outsella-widget");
    if (widgetElement) {
      widgetElement.addEventListener(
        "outsella-widget-open",
        handleWidgetOpen as EventListener
      );
    }

    return () => {
      clearTimeout(timer);
      if (widgetElement) {
        widgetElement.removeEventListener(
          "outsella-widget-open",
          handleWidgetOpen as EventListener
        );
      }
    };
  }, []);

  const handleAnswer = () => {
    setWidgetStage(WidgetScene.CHOICE);
  };
  const isLoading = widgetStage === WidgetScene.LOADING;

  return (
    (config?.ai_agent?.is_deployed || isPreview) && (
      <StoreProvider>
        <motion.div
          ref={widgetRef}
          style={{
            bottom: "10px",
            right: position.endsWith("right") ? "20px" : "auto",
            left: position.endsWith("left") ? "20px" : "auto",
            position: !isPreview ? "fixed" : "static",
            backgroundColor: `${
              isLoading ? config?.main_color || "#0051ff" : ""
            }`,
            zIndex: 999999999999,
          }}
          initial={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            zIndex: 999999999999,
          }}
          animate={{
            width: isLoading ? "40px" : "auto",
            height: isLoading ? "40px" : "auto",
            borderRadius: isLoading ? "50%" : "var(--rounded-widget)",
            zIndex: 999999999999,
          }}
          transition={{ type: "spring", stiffness: 100, damping: 15 }}
          className={className}
        >
          <AnimatePresence mode="wait">
            {!isLoading && (
              <motion.div
                key={widgetStage}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                style={{ height: "100%" }}
              >
                {widgetStage === WidgetScene.INCOMING_CALL && (
                  <IncomingCall config={config!} handleAnswer={handleAnswer} />
                )}
                {widgetStage === WidgetScene.CHOICE && (
                  <Choice
                    config={config!}
                    agentId={agentid}
                    setWidgetStage={setWidgetStage}
                  />
                )}
                {widgetStage === WidgetScene.CALL && (
                  <ChoiceCall
                    config={config!}
                    agentId={agentid}
                    setWidgetStage={setWidgetStage}
                  />
                )}
                {widgetStage === WidgetScene.CHAT && (
                  <ChoiceChat
                    config={config!}
                    agentId={agentid}
                    setWidgetStage={setWidgetStage}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </StoreProvider>
    )
  );
};
